import React, { useState } from "react";
import { Link } from "react-router-dom";
import { LeadCard, Sec, InlineAdd, NextPointer } from "./parts";
import { callsView, callFacts, whenLine, transcriptClock } from "./leadModel.mjs";
import { owedOf } from "../nextModel.mjs";
import { relativeDay, listWords } from "../format.mjs";
import { meetingNotePost } from "../meetingNotes.mjs";
import { tickPath, tickRequest } from "../../callActions.mjs";

/*
 * CALL HELD (In progress): every call, the newest open (the brief's Call held row;
 * matrix 1e: "Every call, newest open · to-dos ours / theirs · promised"). Undrawn
 * as a frame, so built in the drawn card grammar on Gabriel's ruling (2026-10-01),
 * from what FocusPanel's Calls showed (asked 2026-09-29: a company with three calls
 * behind it used to read as though it had one).
 *
 *   - A RECORDED CALL (Fireflies) opens to its action items (what I promised, the
 *     pricing I quoted, what was agreed, what they owe: read from the transcript,
 *     2026-10-13), its summary, who was on it, "Open call" (its page in Meetings ▸
 *     Calls) and the transcript, which opens in the CRM (record/TranscriptDialog).
 *     Fireflies' own to-dos, split ours and theirs by the owner it wrote, stand in
 *     until a call has been read.
 *   - A MEETING NOBODY RECORDED (most of them) says so plainly, and offers Held /
 *     No-show (POST /calendar/outcome, the calendar page's own answer) and Add notes,
 *     drawn as links the way the timeline's meeting row draws them (frame 1a). The
 *     notes are a timeline note carrying the meeting's event id, written in the one
 *     shape the timeline's own Add notes writes (record/meetingNotes.mjs), so they
 *     show here under the meeting and in the timeline beside it.
 *   - "Follow up from this call" on a call there is something to write from (the
 *     recording, or the notes typed against it), while Next has no follow-up line
 *     of its own: the server raises call_followup for the newest call only, and an
 *     older call, or one Next's × hid, still has to be answerable from here. A
 *     link, not a button: Next keeps the page's one yellow.
 *   - Older calls are one line each and open on click, several at once.
 *   - Anything I promised to send points at its Next line ("in Next ↑"), which owns
 *     Send and Mark sent; the next meeting, when one is booked, closes the card.
 *
 * Props:
 *   record   the shell's bundle: timeline.rows (calls, meetings, notes),
 *            act.facts (owed, next_meeting_at), company, contact, zones
 *   ops      ops.meetingOutcome(eventId, outcome), ops.call(path, options),
 *            ops.reload(), ops.openTranscript(activityId, at)
 */
export default function CallHeld({ record, ops }) {
  const calls = callsView(record.timeline.rows, { company: record.company, now: record.now });
  const followupInNext = !!(record.next && record.next.lines.some((l) => l.kind === "call_followup"));
  const facts = (record.act && record.act.facts) || {};
  const owed = owedOf(facts);
  const [open, setOpen] = useState(() => new Set(calls.length ? [calls[0].key] : []));
  const toggle = (key) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // A meeting today or tomorrow is LeadBlock's status line already: not said twice.
  const nextAt = facts.next_meeting_at && !facts.next_meeting_declined && !(record.next && record.next.meeting) ? facts.next_meeting_at : null;

  return (
    <LeadCard label={calls.length > 1 ? `The calls · ${calls.length}` : "The call"} meta={calls[0] ? relativeDay(calls[0].at, { now: record.now }) : null}>
      {calls.length === 0 && (
        <div>
          <div className="rec-headline">No call on record yet</div>
          <div className="rec-body">Nothing on the calendar or in Fireflies for this company so far.</div>
        </div>
      )}
      {calls.length > 0 && (
        <div className="rec-lead__calls">
          {calls.map((c, i) => (
            <CallEntry key={c.key} entry={c} first={i === 0} open={open.has(c.key)} onToggle={() => toggle(c.key)} record={record} ops={ops} followupInNext={followupInNext} />
          ))}
        </div>
      )}
      {(owed || nextAt) && (
        <Sec>
          {owed && (
            <span className="rec-lead__ink">
              <b>Promised:</b> {owed.what} <NextPointer />
            </span>
          )}
          {nextAt && (
            <span className="rec-lead__ink">
              <b>Next meeting:</b> {whenLine(nextAt, record.zones, { now: record.now })}
              {facts.next_meeting_title ? ` · ${facts.next_meeting_title}` : ""}
            </span>
          )}
        </Sec>
      )}
    </LeadCard>
  );
}

function CallEntry({ entry: c, first, open, onToggle, record, ops, followupInNext }) {
  const [error, setError] = useState(null);
  const [followError, setFollowError] = useState(null);
  const facts = callFacts(c, record.zones, { now: record.now });
  const mark = (outcome) => {
    setError(null);
    return Promise.resolve(ops.meetingOutcome(c.event_id, outcome)).catch((e) => setError(e.message));
  };
  const addNotes = async (body) => {
    const co = record.company || {};
    const post = meetingNotePost({
      meeting: { event_id: c.event_id, occurred_at: c.at, title: c.title },
      body,
      contactId: record.contact ? record.contact.id : null,
      companyId: co.id || null,
    });
    if (!post) throw new Error("Nothing to save yet.");
    await ops.call("/activities", { method: "POST", body: JSON.stringify(post) });
    await ops.reload();
  };
  // The draft is written from THIS call (activity_id): its recording, or the
  // notes typed against it. Addressed to whoever the call row is filed on.
  const followUp = () => {
    setFollowError(null);
    try {
      ops.compose({
        kind: "call_followup",
        action_kind: "call_followup",
        verb: "Follow up",
        activity_id: c.activity_id,
        contact_id: c.contact_id || (record.contact ? record.contact.id : null),
        line_key: `call_followup:${c.activity_id}`,
      });
    } catch (e) {
      setFollowError(e.message);
    }
  };
  const canFollowUp = !followupInNext && !!c.activity_id;

  if (!open) {
    return (
      <button type="button" className={`rec-fold rec-lead__fold${first ? "" : " rec-lead__fold--older"}`} aria-expanded={false} onClick={onToggle}>
        <span className="rec-lead__chev" aria-hidden="true">▸</span>
        <span className="rec-strong">{c.title}</span>
        <span className="rec-sub rec-lead__foldsub">{facts}</span>
      </button>
    );
  }

  const unrecorded = c.kind === "meeting";
  return (
    <div className={first ? "" : "rec-lead__sec"}>
      <button type="button" className="rec-lead__callhead" aria-expanded={true} onClick={onToggle}>
        <span className="rec-lead__chev" aria-hidden="true">▾</span>
        <span className="rec-headline">{c.title}</span>
      </button>
      <div className="rec-body">
        {facts}
        {c.call_id && (
          <>
            {" · "}
            <Link to={`/meetings/calls/${encodeURIComponent(c.call_id)}`}>Open call</Link>
          </>
        )}
        {c.in_crm_transcript ? (
          <>
            {" · "}
            <button type="button" className="rec-link" onClick={() => ops.openTranscript(c.activity_id)}>Transcript</button>
          </>
        ) : (
          c.transcript && (
            <>
              {" · "}
              <a href={c.transcript} target="_blank" rel="noopener noreferrer">Transcript ↗</a>
            </>
          )
        )}
      </div>
      {c.attendees.length > 0 && <div className="rec-meta">With {listWords(c.attendees)}</div>}
      {!unrecorded && <ActionItems entry={c} ops={ops} />}
      {c.points.length > 0 && (
        <ul className="rec-lead__points">
          {c.points.map((p, j) => (
            <li key={j}>
              {p.label ? <b>{p.label}: </b> : null}
              {p.text}
            </li>
          ))}
        </ul>
      )}
      {c.actions.state === "unread" && (c.todos.ours.length > 0 || c.todos.theirs.length > 0) && (
        <div className="rec-kv rec-kv--96 rec-lead__todos">
          <span>Ours</span>
          <span>{c.todos.ours.join(" · ") || "nothing"}</span>
          <span>Theirs</span>
          <span>{c.todos.theirs.join(" · ") || "nothing"}</span>
        </div>
      )}
      {c.notes.map((n) => (
        <div key={n.id} className="rec-lead__note">
          <span className="rec-lead__small">Your notes</span>
          <div className="rec-lead__notebody">{n.body}</div>
        </div>
      ))}
      {unrecorded && !c.outcome && (
        <div className="rec-inline rec-lead__controls">
          <button type="button" className="rec-link rec-lead__link" onClick={() => mark("held")}>Held</button>
          <button type="button" className="rec-link rec-lead__link" onClick={() => mark("no_show")}>No-show</button>
          {error && <span className="rec-err">{error}</span>}
        </div>
      )}
      {unrecorded && !c.notes.length && (
        <div className="rec-lead__controls">
          <InlineAdd label="Add notes" multiline placeholder="What was said, what you owe them, what they owe you" onSave={addNotes} />
        </div>
      )}
      {canFollowUp && (
        <div className="rec-inline rec-lead__controls">
          <button type="button" className="rec-link rec-lead__link" onClick={followUp}>Follow up from this call</button>
          {followError && <span className="rec-err">{followError}</span>}
        </div>
      )}
    </div>
  );
}

/*
 * ACTION ITEMS: what he promised, the pricing he quoted, what was agreed and what
 * they will do, read from the transcript (leadModel.mjs actionItemsOf). Above the
 * summary, because it is the part of the call he acts on. Each item's time opens
 * the transcript on the line it was said, and its quote is on hover.
 *   - nothing found: says so, so an empty card is a reading and not a gap
 *   - too little transcript: says why there is nothing, rather than "nothing"
 *   - not read yet (an older call): nothing here; Fireflies' Ours / Theirs below
 * A ticked item stays, struck through, with Undo beside "done" (asked 2026-10-27:
 * a tick made by accident had no way back).
 */
function ActionItems({ entry: c, ops }) {
  const a = c.actions;
  const [undoing, setUndoing] = useState(null);
  const [undoError, setUndoError] = useState(null);
  const undo = async (it) => {
    setUndoing(it.key);
    setUndoError(null);
    try {
      await ops.call(tickPath({ activity_id: c.activity_id }), tickRequest(it, false));
      await ops.reload();
    } catch (e) {
      setUndoError(e.message);
    } finally {
      setUndoing(null);
    }
  };
  if (a.state === "unread") return null;
  if (a.state === "thin") {
    return (
      <div className="rec-lead__actions">
        <span className="rec-lead__small">Action items</span>
        <div className="rec-meta">
          Fireflies caught only {a.coverage.words.toLocaleString()} words of {a.coverage.minutes} minutes, so nothing could be read from this call. Usually the transcription language is wrong for it.
        </div>
      </div>
    );
  }
  return (
    <div className="rec-lead__actions">
      <span className="rec-lead__small">Action items</span>
      {a.groups.length === 0 ? (
        <div className="rec-meta">Nothing promised, quoted or agreed on the call.</div>
      ) : (
        <div className="rec-kv rec-kv--96">
          {a.groups.map((g) => (
            <React.Fragment key={g.label}>
              <span>{g.label}</span>
              <ul className="rec-lead__promises">
                {g.items.map((it, i) => (
                  <li key={i} title={it.quote ? `“${it.quote}”` : undefined} className={it.done_at ? "is-done" : undefined}>
                    {/* Ticked off in Next or on Today (src/calls/promises.js). */}
                    {it.done_at ? (
                      <>
                        <s>{it.text}</s> <span className="rec-meta">done</span>
                        {it.key && (
                          <>
                            {" "}
                            <button type="button" className="rec-link rec-lead__at" disabled={undoing === it.key} onClick={() => undo(it)}>
                              Undo
                            </button>
                          </>
                        )}
                      </>
                    ) : it.text}
                    {it.at !== null && c.in_crm_transcript && (
                      <>
                        {" "}
                        <button type="button" className="rec-link rec-lead__at" aria-label={`Open the transcript at ${transcriptClock(it.at)}`} onClick={() => ops.openTranscript(c.activity_id, it.at)}>
                          {transcriptClock(it.at)}
                        </button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </React.Fragment>
          ))}
        </div>
      )}
      {undoError && <span className="rec-err">{undoError}</span>}
    </div>
  );
}
